import { useCallback, useEffect, useRef, useState } from 'react'

import {
  logAIGenerationError,
  normalizeAISuggestion,
  pollForGenerationResult,
} from '@/hooks/services/aiGenerationService'
import { useGlobalState } from '@/hooks/useGlobalState'
import { isInformationOnlyResourceType } from '@/services/api/api.types'

import type { CMFormSection } from './ContentModuleFormScreen'
import type { FAQSectionRef } from './FAQSection'
import { useStepResources } from './useStepResources'

type DescriptionAIState = 'idle' | 'loading' | 'pending'
export type ContentAccessType = 'full_access' | 'information_only' | 'media_only'

export type ThumbnailDraft = { kind: 'unchanged' } | { kind: 'replaced'; s3Key: string } | { kind: 'removed' }

export function useReviewModalState(props: CMFormSection) {
  const { api } = useGlobalState()
  const { isMediaMode } = props

  const [pendingFileName, setPendingFileName] = useState<string | null>(null)
  // Media mode locks to full_access (picker is hidden).
  const [contentAccessType, setContentAccessType] = useState<ContentAccessType>(
    isMediaMode ? 'full_access' : 'information_only',
  )
  const [pendingDescription, setPendingDescription] = useState<string | null>(null)
  const [thumbnailDraft, setThumbnailDraft] = useState<ThumbnailDraft>({ kind: 'unchanged' })

  const [descriptionAIState, setDescriptionAIState] = useState<DescriptionAIState>('idle')
  const [descriptionSuggestion, setDescriptionSuggestion] = useState<string | null>(null)

  const autoGenerationTriggeredRef = useRef<string | null>(null)
  const descriptionAutoGenTriggeredRef = useRef<string | null>(null)
  // One-shot per step. `'ai-applied'` (media mode only) tells the resource-init
  // effect not to blank out the descriptionAi seed when the resource has no
  // description of its own.
  const descriptionSeedRef = useRef<{ stepId: string; source: 'evaluated' | 'ai-applied' } | null>(null)
  const accessTypeInitRef = useRef<string | null>(null)
  const youtubeTitleSeedRef = useRef<string | null>(null)
  const descriptionAbortRef = useRef<AbortController | null>(null)
  const faqSectionRef = useRef<FAQSectionRef>(null)

  const { stepResources, stepResourcesSettled, uploadedFileResource, sourceFileResource } = useStepResources(
    props.stepId,
    props.stepContentQuery.data?.sourceUrl,
  )

  const hasContent = !!props.stepContentQuery.data
  const hasExtractedContent = !!props.stepContentQuery.data?.content?.trim()

  const showInApp = contentAccessType === 'full_access' || contentAccessType === 'media_only'
  const useInRag = contentAccessType === 'full_access' || contentAccessType === 'information_only'

  // Seed description from task_steps.descriptionAi when blank (once per step).
  // Media mode writes the value directly; otherwise it goes through accept/reject.
  useEffect(() => {
    if (!props.stepId || !props.stepsQuery?.data) return
    if (stepResources === undefined) return
    if (descriptionSeedRef.current?.stepId === props.stepId) return

    const step = props.stepsQuery.data.find(s => s.stepId === props.stepId)
    if (!step?.descriptionAi?.trim()) return

    const descriptionBlank = !(pendingDescription?.trim() || sourceFileResource?.description?.trim())
    if (!descriptionBlank || descriptionAIState !== 'idle') {
      // Mark evaluated even on skip — keeps this effect one-shot per step.
      descriptionSeedRef.current = { stepId: props.stepId, source: 'evaluated' }
      return
    }

    if (isMediaMode) {
      setPendingDescription(step.descriptionAi.trim())
      descriptionSeedRef.current = { stepId: props.stepId, source: 'ai-applied' }
      return
    }

    setDescriptionSuggestion(step.descriptionAi.trim())
    setDescriptionAIState('pending')
    descriptionSeedRef.current = { stepId: props.stepId, source: 'evaluated' }
  }, [
    props.stepId,
    props.stepsQuery?.data,
    pendingDescription,
    sourceFileResource?.description,
    descriptionAIState,
    stepResources,
    isMediaMode,
  ])

  // Initialize description from the source file resource. Skip when the seed
  // ref shows we already applied a descriptionAi value (media mode).
  useEffect(() => {
    if (!sourceFileResource) return
    const resourceDesc = sourceFileResource.description?.trim() ?? ''
    const seed = descriptionSeedRef.current
    const aiSeededHere = seed != null && seed.stepId === props.stepId && seed.source === 'ai-applied'
    if (aiSeededHere && !resourceDesc) return
    setPendingDescription(sourceFileResource.description ?? '')
  }, [sourceFileResource?.resourceId, sourceFileResource?.description, props.stepId])

  // Initialize contentAccessType from resource + step data (once per resource).
  // Skipped in media mode (picker is hidden; value is locked to full_access).
  useEffect(() => {
    if (!sourceFileResource) return

    // Lock to Information Only even in media mode (which defaults to full_access).
    if (isInformationOnlyResourceType(sourceFileResource.type)) {
      setContentAccessType('information_only')
      return
    }

    if (isMediaMode) return

    const resourceKey = `${sourceFileResource.resourceId}-${sourceFileResource.showInApp}-${sourceFileResource.showInAppSupported}`
    if (accessTypeInitRef.current === resourceKey) return
    accessTypeInitRef.current = resourceKey

    if (!sourceFileResource.showInAppSupported) {
      setContentAccessType('information_only')
      return
    }

    const stepData = props.stepsQuery.data?.find(s => s.stepId === props.stepId)
    const stepUseInRag = stepData?.useInRag ?? true
    const resourceShowInApp = sourceFileResource.showInApp ?? false

    if (resourceShowInApp && stepUseInRag) {
      setContentAccessType('full_access')
    } else if (resourceShowInApp && !stepUseInRag) {
      setContentAccessType('media_only')
    } else {
      setContentAccessType('information_only')
    }
  }, [
    sourceFileResource?.resourceId,
    sourceFileResource?.type,
    sourceFileResource?.showInApp,
    sourceFileResource?.showInAppSupported,
    props.stepsQuery.data,
    props.stepId,
    isMediaMode,
  ])

  async function handleDescriptionNetworkError(abortController: AbortController, stepId: string) {
    const pollResult = await pollForGenerationResult(
      api,
      { entityType: 'step', entityId: stepId, systemPromptTemplateId: 'generate_cm_file_description' },
      { signal: abortController.signal },
    )
    if (pollResult.status === 'done') {
      const suggestion = normalizeAISuggestion(pollResult.result)
      setDescriptionSuggestion(suggestion)
      setDescriptionAIState('pending')
    } else if (pollResult.status === 'aborted') {
      // Cancelled by user — state already reset
    } else {
      const reason = pollResult.status === 'failed' ? pollResult.error : 'Polling timed out'
      logAIGenerationError(reason, null)
      setDescriptionAIState('idle')
    }
  }

  async function generateSourceFileDescription() {
    if (!props.stepId) return

    const abortController = new AbortController()
    descriptionAbortRef.current = abortController
    setDescriptionAIState('loading')

    try {
      const response = await api.safeRequest(api.generate, {
        step_id: props.stepId,
        system_prompt_template_id: 'generate_cm_file_description',
        message_template_id: 'generate_step_message',
        extra_data: {
          accepted_data: pendingDescription || undefined,
        },
      })

      if (abortController.signal.aborted) return

      if (response?.ok && response.data) {
        const suggestion = normalizeAISuggestion(response.data as string)
        setDescriptionSuggestion(suggestion)
        setDescriptionAIState('pending')
      } else if (!response.ok && response.isNetworkError) {
        await handleDescriptionNetworkError(abortController, props.stepId)
      } else {
        setDescriptionAIState('idle')
      }
    } catch {
      setDescriptionAIState('idle')
    } finally {
      if (descriptionAbortRef.current === abortController) {
        descriptionAbortRef.current = null
      }
    }
  }

  async function generateSourceFileDescriptionWithGuide(instructions: string) {
    if (!props.stepId) return

    const abortController = new AbortController()
    descriptionAbortRef.current = abortController
    setDescriptionAIState('loading')

    try {
      const extraData: Record<string, string | undefined> = {
        accepted_data: pendingDescription || undefined,
        user_instructions: instructions.trim() || undefined,
      }
      if (descriptionAIState === 'pending' && descriptionSuggestion) {
        extraData.proposed_data = descriptionSuggestion
      }

      const response = await api.safeRequest(api.generate, {
        step_id: props.stepId,
        system_prompt_template_id: 'generate_cm_file_description',
        message_template_id: 'generate_step_message',
        extra_data: extraData,
      })

      if (abortController.signal.aborted) return

      if (response?.ok && response.data) {
        const suggestion = normalizeAISuggestion(response.data as string)
        setDescriptionSuggestion(suggestion)
        setDescriptionAIState('pending')
      } else if (!response.ok && response.isNetworkError) {
        await handleDescriptionNetworkError(abortController, props.stepId)
      } else {
        setDescriptionAIState('idle')
      }
    } catch {
      setDescriptionAIState('idle')
    } finally {
      if (descriptionAbortRef.current === abortController) {
        descriptionAbortRef.current = null
      }
    }
  }

  function acceptDescriptionSuggestion() {
    if (descriptionSuggestion) {
      setPendingDescription(descriptionSuggestion)
      setDescriptionSuggestion(null)
      setDescriptionAIState('idle')
    }
  }

  function rejectDescriptionSuggestion() {
    setDescriptionSuggestion(null)
    setDescriptionAIState('idle')
  }

  function cancelDescriptionGeneration() {
    descriptionAbortRef.current?.abort()
    descriptionAbortRef.current = null
    setDescriptionSuggestion(null)
    setDescriptionAIState('idle')
  }

  const isMutating = props.stepUpdateMutation.isPending

  const markThumbnailReplaced = useCallback((s3Key: string) => {
    setThumbnailDraft({ kind: 'replaced', s3Key })
  }, [])

  const markThumbnailRemoved = useCallback(() => {
    setThumbnailDraft({ kind: 'removed' })
  }, [])

  const clearThumbnailDraft = useCallback(() => {
    setThumbnailDraft({ kind: 'unchanged' })
  }, [])

  // Drop pending thumbnail state when the step changes so a pending upload
  // from one step can't leak into another if the modal instance is reused.
  useEffect(() => {
    setThumbnailDraft({ kind: 'unchanged' })
  }, [props.stepId])

  return {
    stepResources,
    stepResourcesSettled,

    pendingFileName,
    setPendingFileName,
    contentAccessType,
    setContentAccessType,
    showInApp,
    useInRag,
    pendingDescription,
    setPendingDescription,
    thumbnailDraft,
    markThumbnailReplaced,
    markThumbnailRemoved,
    clearThumbnailDraft,

    descriptionAIState,
    descriptionSuggestion,
    generateSourceFileDescription,
    generateSourceFileDescriptionWithGuide,
    acceptDescriptionSuggestion,
    rejectDescriptionSuggestion,
    cancelDescriptionGeneration,

    autoGenerationTriggeredRef,
    descriptionAutoGenTriggeredRef,
    youtubeTitleSeedRef,
    faqSectionRef,

    hasContent,
    hasExtractedContent,
    isMutating,
    uploadedFileResource,
    sourceFileResource,
  }
}
